import React, { useState, useMemo, useEffect } from "react";
import Card from "./Card";
import BarGraph from "../Governance/BarGraph";
import SDGCard from "./SDGCard";
import TextStyles from "../../../../../components/Elements/TextStyles/TextStyles";
import { ReactComponent as Back } from "../../../../../assets/svgs/back-button.svg";
import { THEME_COLORS } from "../../../../../color";
import { useNavigate } from "react-router-dom";
import TimeFilterBar from "../TimeFilterBar";
import { useSelector } from "react-redux";
import PermissionForm from "../../../../Auth/PermissionForm";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";

const SDG_IMAGES = {
  1: "/sdg/sd1d.svg",
  2: "/sdg/sd2d.svg",
  3: "/sdg/sd3d.svg",
  4: "/sdg/sd4d.svg",
  5: "/sdg/sd5d.svg",
  6: "/sdg/sd6d.svg",
  7: "/sdg/sd7d.svg",
  8: "/sdg/sdnew8.svg",
  9: "/sdg/sdgnew13.svg",
  10: "/sdg/sdgnew10.svg",
  11: "/sdg/sdgnew11.svg",
  12: "/sdg/sdg12.svg",
  13: "/sdg/sdgnew12.svg",
  14: "/sdg/sdgnew14.svg",
  15: "/sdg/sdgnew15.svg",
  16: "/sdg/sdgnew16.svg",
  17: "/sdg/sdgnew17.svg",
};

const SDG_TITLES = {
  1: "No Poverty",
  2: "Zero Hunger",
  3: "Good Health",
  4: "Quality Education",
  5: "Gender Equality",
  6: "Clean Water",
  7: "Affordable Energy",
  8: "Decent Work",
  9: "Industry & Innovation",
  10: "Reduced Inequalities",
  11: "Sustainable Cities",
  12: "Responsible Consumption",
  13: "Climate Action",
  14: "Life Below Water",
  15: "Life On Land",
  16: "Peace & Justice",
  17: "Partnerships",
};

const SDG_DUMMY_BAR_VALUES = {
  1: [28, 14, 9, 17, 5],
  2: [18, 16, 11, 19, 7],
  3: [22, 13, 18, 12, 8],
  4: [24, 19, 10, 15, 6],
  5: [20, 17, 14, 16, 9],
  6: [16, 12, 20, 14, 7],
  7: [26, 15, 13, 18, 8],
  8: [21, 18, 16, 20, 10],
  9: [19, 21, 15, 17, 11],
  10: [17, 14, 19, 13, 12],
  11: [23, 18, 16, 14, 9],
  12: [15, 20, 22, 11, 10],
  13: [12, 16, 24, 15, 9],
  14: [10, 14, 18, 20, 8],
  15: [11, 13, 17, 19, 7],
  16: [25, 12, 14, 16, 6],
  17: [27, 15, 13, 18, 10],
};

const SDG_BAR_LABELS = [
  "SOCIAL",
  "GOVERNANCE",
  "ENVIRONMENTAL",
  "ECONOMIC",
  "OTHER",
];

// Fixed 1..17 list so the grid always has something to render regardless of
// what the API returns (full data, partial data, or nothing at all).
const ALL_SDG_NUMBERS = Array.from({ length: 17 }, (_, i) => i + 1);

const parseSdgNumber = (sdgVal) => {
  if (sdgVal === null || sdgVal === undefined) return null;
  if (typeof sdgVal === "number") return sdgVal;
  const str = String(sdgVal).trim();
  if (!str) return null;
  const digitMatch = str.match(/\d+/);
  return digitMatch ? parseInt(digitMatch[0], 10) : null;
};

const buildDummySDGDashboard = () => {
  const components = [];

  for (let i = 1; i <= 17; i += 1) {
    components.push({
      componentId: `sdg_${i}_avg_score`,
      type: "kpi_card",
      sortOrder: i - 1,
      displayOptions: { sdgNumber: i, sdgTitle: SDG_TITLES[i] || `SDG ${i}` },
      data: { value: Number((60 + i * 1.8).toFixed(2)) },
    });

    components.push({
      componentId: `sdg_${i}_subsection_chart`,
      type: "bar_chart",
      sortOrder: i - 1,
      displayOptions: { sdgNumber: i },
      data: {
        labels: SDG_BAR_LABELS,
        values: SDG_DUMMY_BAR_VALUES[i] || [10, 10, 10, 10, 10],
      },
    });
  }

  components.push({
    componentId: "sdg_key_performance",
    type: "kpi_card",
    displayOptions: {},
    data: { sdgFirst: "sdg_1", sdgSecond: "sdg_2" },
  });

  return {
    dashboardId: "sdg_dashboard",
    title: "SDG",
    description: "Dummy SDG dashboard data for preview",
    components,
  };
};

const DUMMY_DASHBOARD_DATA = buildDummySDGDashboard();

const SDG = () => {
  const navigate = useNavigate();
  const [showPermission, setPermission] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeDashboard, setActiveDashboard] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true" ? "live" : "dummy";
  });
  const [loading, setLoading] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true";
  });
  const { user, selectedGroupMember } = useSelector((state) => state.auth);
  const [selectedSDG, setSelectedSDG] = useState(1);
  const [yearFilter, setYearFilter] = useState(() =>
    user?.reportingYearType === "calendar" ? "2026" : "2026-2027",
  );
  const isPermissionCompleted = user?.isCompleted;
  const selectedMemberId =
    typeof selectedGroupMember === "string"
      ? selectedGroupMember
      : selectedGroupMember?.clientId ||
        selectedGroupMember?._id ||
        selectedGroupMember?.client_id?._id ||
        selectedGroupMember?.client_id;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (activeDashboard !== "live") {
        setLoading(false);
        setDashboardData(null);
        return;
      }

      setLoading(true);

      try {
        let apiUrl;

        if (selectedMemberId) {
          apiUrl = API_URLS.GET_GROUP_MEMBER_DASHBOARD(
            "sdg_dashboard",
            selectedMemberId,
          );
        } else {
          apiUrl = API_URLS.GET_SDG_DASHBOARD("sdg_dashboard");
        }

        const queryParams = new URLSearchParams();
        if (yearFilter) queryParams.append("year", yearFilter);
        const queryString = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        apiUrl += queryString;

        const res = await getApi({ apiUrl });

        // Same handling whether the response is missing, empty, or has no
        // components — dashboardData simply stays null and the grid below
        // renders its normal "no data" placeholder state.
        const data = res?.data
          ? res.data.dashboard
            ? res.data.dashboard
            : res.data
          : null;
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching SDG dashboard data:", error);
        // API failure is treated exactly like "no data" — dashboardData
        // stays null, so the UI falls back to the same empty/placeholder
        // state used when the API simply returns nothing.
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [selectedMemberId, activeDashboard, yearFilter]);

  useEffect(() => {
    if (!isPermissionCompleted) {
      setPermission(true);
    }
  }, [isPermissionCompleted]);

  // Always resolves to a valid dashboard object with a components array,
  // whether we're in dummy mode, live-success mode, or live-failure mode
  // (failure and "no data" both land here as { components: [] }).
  const dashboardSource =
    activeDashboard === "live"
      ? dashboardData || { components: [] }
      : DUMMY_DASHBOARD_DATA;

  const getComponentData = (type, sdgNumber) => {
    return dashboardSource?.components?.find(
      (c) =>
        (c.type === type ||
          c.componentId === `sdg_${sdgNumber}_avg_score` ||
          c.componentId === `sdg_${sdgNumber}_subsection_chart`) &&
        c.displayOptions?.sdgNumber === Number(sdgNumber),
    );
  };

  const sdgKpis = useMemo(() => {
    return (
      dashboardSource?.components?.filter(
        (c) =>
          (c.componentId?.endsWith("_avg_score") ||
            c.componentId?.startsWith("sdg_kpi_") ||
            c.type === "kpi_card") &&
          c.displayOptions?.sdgNumber,
      ) || []
    ).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [dashboardSource]);

  const keyPerformanceComp = useMemo(() => {
    return dashboardSource?.components?.find(
      (c) => c.componentId === "sdg_key_performance",
    );
  }, [dashboardSource]);

  const highestSdgComponents = useMemo(() => {
    return [...sdgKpis].sort(
      (a, b) => (b.data?.value || 0) - (a.data?.value || 0),
    );
  }, [sdgKpis]);

  const findSdgComponent = (sdgVal) => {
    const sdgNum = parseSdgNumber(sdgVal);
    if (!sdgNum) return null;
    return sdgKpis.find(
      (c) =>
        c.displayOptions?.sdgNumber === sdgNum ||
        c.componentId === `sdg_${sdgNum}_avg_score` ||
        c.componentId === `sdg_kpi_${sdgNum}`,
    );
  };

  const keySdg1Comp = useMemo(() => {
    const rawVal = keyPerformanceComp?.data?.sdgFirst;
    const matched = findSdgComponent(rawVal);
    if (matched) return matched;
    return highestSdgComponents[0] || null;
  }, [keyPerformanceComp, sdgKpis, highestSdgComponents]);

  const keySdg2Comp = useMemo(() => {
    const rawVal = keyPerformanceComp?.data?.sdgSecond;
    const matched = findSdgComponent(rawVal);
    if (matched) return matched;
    if (highestSdgComponents.length > 1) {
      return highestSdgComponents[1];
    }
    return highestSdgComponents[0] || null;
  }, [keyPerformanceComp, sdgKpis, highestSdgComponents]);

  // Grid is now built from the fixed 1..17 list, not solely from whatever
  // sdgKpis the API happened to return. Any SDG number missing a matching
  // component still gets a card (with a 0.00 placeholder value) so the
  // images never disappear, even on partial/failed API responses.
  const orderedSdgKpis = useMemo(() => {
    return ALL_SDG_NUMBERS.filter((num) => num !== selectedSDG).map((num) => {
      const match = sdgKpis.find(
        (kpi) => kpi.displayOptions?.sdgNumber === num,
      );
      return (
        match || {
          componentId: `sdg_${num}_placeholder`,
          type: "kpi_card",
          displayOptions: {
            sdgNumber: num,
            sdgTitle: SDG_TITLES[num] || `SDG ${num}`,
          },
          data: { value: 0 },
        }
      );
    });
  }, [sdgKpis, selectedSDG]);

  const selectedSDGKpi = getComponentData("kpi_card", selectedSDG) || {
    displayOptions: {
      sdgNumber: selectedSDG,
      sdgTitle: SDG_TITLES[selectedSDG],
    },
    data: { value: 0 },
  };
  const selectedSDGChart = getComponentData("bar_chart", selectedSDG);

  const averageSDGScore =
    sdgKpis.length > 0
      ? (
          sdgKpis.reduce((acc, curr) => acc + (curr.data?.value || 0), 0) /
          sdgKpis.length
        ).toFixed(2)
      : "0.00";

  const chartData = useMemo(() => {
    const defaultLabels = [
      "SOCIAL",
      "GOVERNANCE",
      "ENVIRONMENTAL",
      "ECONOMIC",
      "OTHER",
    ];
    const apiLabels = (selectedSDGChart?.data?.labels || []).map((l) =>
      l.toUpperCase(),
    );
    const apiValues = selectedSDGChart?.data?.values || [];

    const dataMap = {};
    apiLabels.forEach((label, index) => {
      let mappedLabel = "OTHER";
      if (label.includes("SOCIAL")) mappedLabel = "SOCIAL";
      else if (label.includes("GOVERNANCE")) mappedLabel = "GOVERNANCE";
      else if (label.includes("ENVIRONMENT")) mappedLabel = "ENVIRONMENTAL";
      else if (label.includes("ECONOMIC")) mappedLabel = "ECONOMIC";

      dataMap[mappedLabel] =
        (dataMap[mappedLabel] || 0) + (apiValues[index] || 0);
    });

    return defaultLabels.map((label) => {
      const val = dataMap[label] !== undefined ? dataMap[label] : 0;
      return {
        name: label,
        value: val === 0 ? 2 : val,
        actualValue: val,
      };
    });
  }, [selectedSDGChart]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#50BAC1]"></div>
      </div>
    );
  }

  return (
    <div className="md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6">
      {!isPermissionCompleted && showPermission && (
        <PermissionForm permissionConClose={() => setPermission(false)} />
      )}
      <TimeFilterBar
        title=" SDG"
        showBreadcrumb={true}
        breadcrumbItems={["Home", " SDG"]}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
        activeDashboard={activeDashboard}
        onDashboardChange={setActiveDashboard}
        onYearChange={setYearFilter}
        selectedYearValue={yearFilter}
      />
      <div
        className={` ${
          !isPermissionCompleted ? "blur-sm opacity-20 pointer-events-none" : ""
        }`}
      >
        {/* Top Cards */}
        <div className="flex flex-col md:flex-row flex-wrap gap-3 bg-white p-4 border border-[#3DA3BA59] rounded-lg mt-2">
          <Card label="Average SDG Score" value={averageSDGScore} />
          <Card
            label={`Key SDG 1 Performance`}
            value={keySdg1Comp?.data?.value?.toFixed(2) || "0.00"}
          />
          <Card
            label={`Key SDG 2 Performance`}
            value={keySdg2Comp?.data?.value?.toFixed(2) || "0.00"}
          />
          <Card label={<>Key SDG's Average Score</>} value={averageSDGScore} />
        </div>

        {/* Bar Graph + SDG Icon section */}
        <div className="flex flex-col lg:flex-row gap-6 bg-white p-4 mt-6 border border-[#50BAC1] rounded-lg">
          {/* Left column — fixed width, never grows/shrinks */}
          <div className="flex flex-row lg:flex-col items-center justify-center w-full lg:w-[180px] flex-shrink-0 gap-4 lg:gap-0">
            {/* SDG Icon — fixed height container */}
            <div className="flex flex-col items-center lg:h-[180px] lg:justify-end">
              <div
                className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 bg-no-repeat bg-cover bg-center flex-shrink-0"
                style={{
                  backgroundImage: `url("${SDG_IMAGES[selectedSDG]}")`,
                }}
              />
            </div>

            {/* Title — fixed height container so score box never shifts */}
            <div className="hidden lg:flex items-center justify-center w-[160px] h-[2.5rem] mt-2 mb-2">
              <div
                className="text-center font-semibold leading-tight"
                style={{
                  fontSize:
                    selectedSDG === 9 || selectedSDG === 12 ? "13px" : "15px",
                }}
              >
                {selectedSDGKpi?.displayOptions?.sdgTitle || "SDG"}
              </div>
            </div>

            {/* Mobile only title */}
            <div className="flex lg:hidden flex-col items-center">
              <div
                className="text-center font-semibold leading-tight w-[80px]"
                style={{
                  fontSize:
                    selectedSDG === 9 || selectedSDG === 12 ? "13px" : "15px",
                }}
              >
                {selectedSDGKpi?.displayOptions?.sdgTitle || "SDG"}
              </div>
            </div>

            {/* Score Box — always at fixed position on desktop */}
            <div
              className="text-white w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 p-2 md:p-3 lg:p-4 flex flex-col justify-center items-center flex-shrink-0"
              style={{ backgroundColor: THEME_COLORS.green }}
            >
              <div className="text-[11px] sm:text-xs md:text-sm lg:text-sm text-center leading-tight">
                Total Score
              </div>
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl mt-1 lg:mt-2">
                {selectedSDGKpi?.data?.value?.toFixed(2) || "0.00"}
              </div>
            </div>
          </div>

          {/* Graph */}
          <div className="flex-grow">
            <BarGraph
              width={"100%"}
              height={400}
              data={chartData}
              options={{
                plotOptions: {
                  bar: {
                    horizontal: false,
                    columnWidth: "45%",
                    distributed: true,
                    dataLabels: {
                      position: "top",
                    },
                  },
                },
                colors: ["#458276", "#EABF5E", "#69A1F6", "#EBAB9E", "#5BB0C2"],
                dataLabels: {
                  enabled: true,
                  formatter: (val, opts) => {
                    return chartData[opts.dataPointIndex]?.actualValue;
                  },
                  style: {
                    fontSize: "14px",
                    fontWeight: "bold",
                    colors: ["#304758"],
                  },
                  offsetY: -20,
                },
                xaxis: {
                  categories: chartData.map((d) => d.name),
                  labels: {
                    style: {
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    },
                  },
                },

                yaxis: {
                  labels: {
                    formatter: (val) => `${val}`,
                  },
                  min: 0,
                  max: 100,
                  tickAmount: 10,
                },
                tooltip: {
                  enabled: true,
                  y: {
                    formatter: (val, opts) => {
                      return chartData[opts.dataPointIndex]?.actualValue;
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* SDG Grid */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 p-4 mt-6 bg-white rounded-md border border-[#3DA3BA59] mb-10">
          {orderedSdgKpis.map((kpi, index) => {
            const sdgNum = kpi.displayOptions?.sdgNumber;
            const displayValue = kpi.data?.value?.toFixed(2) || "0.00";

            return (
              <SDGCard
                key={kpi.componentId || index}
                url={SDG_IMAGES[sdgNum]}
                value={displayValue}
                onClick={() => setSelectedSDG(sdgNum)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SDG;
